import { useId } from 'react';
import {
  useStorefrontCustomProductCollectionPickerOptions,
  WEBSITE_CONTACT_METHODS,
  WEBSITE_STORE_PAGE_LINKS,
  type SupabaseClient,
  type WebsiteContactMethod,
  type WebsiteStorePageLink,
} from '@dukanti/shared';
import { useTranslation, type TranslationKey } from '@dukanti/shared/i18n';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/features/website-templates/components/native-select';
import { PickerPrompt, PickerSelection, type PickerPromptTone } from './picker-parts';

/** "Store page" offers every page except Sales, which has its own card (PD9). */
export const STORE_PAGE_CHOICES = WEBSITE_STORE_PAGE_LINKS.filter(
  (page): page is Exclude<WebsiteStorePageLink, 'sales'> => page !== 'sales',
);

/** Email is never offered (PD9); a stored email shows as unavailable. */
export const CONTACT_CHOICES = WEBSITE_CONTACT_METHODS.filter(
  (method): method is Exclude<WebsiteContactMethod, 'email'> => method !== 'email',
);

const PAGE_KEYS: Readonly<Record<(typeof STORE_PAGE_CHOICES)[number], TranslationKey>> = {
  home: 'admin.destination.page.home',
  catalog: 'admin.destination.page.catalog',
  shop: 'admin.destination.page.shop',
  'store-info': 'admin.destination.page.storeInfo',
};

const METHOD_KEYS: Readonly<Record<(typeof CONTACT_CHOICES)[number], TranslationKey>> = {
  whatsapp: 'admin.destination.method.whatsapp',
  phone: 'admin.destination.method.phone',
};

export function StorePageSelect({
  page,
  onChange,
  testIdPrefix,
}: {
  readonly page: WebsiteStorePageLink;
  readonly onChange: (page: WebsiteStorePageLink) => void;
  readonly testIdPrefix: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-store-page`}>
      <Label htmlFor={id} className="block text-start">
        {t('admin.destination.storePageLabel')}
      </Label>
      <NativeSelect
        id={id}
        value={page}
        options={STORE_PAGE_CHOICES.map((choice) => ({
          value: choice,
          label: t(PAGE_KEYS[choice]),
        }))}
        onChange={(next) => onChange(next as WebsiteStorePageLink)}
      />
    </div>
  );
}

export function ContactMethodSelect({
  method,
  onChange,
  testIdPrefix,
}: {
  readonly method: WebsiteContactMethod;
  readonly onChange: (method: WebsiteContactMethod) => void;
  readonly testIdPrefix: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const offered = method !== 'email';
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-contact`}>
      {offered ? null : (
        <PickerSelection
          text={t('admin.destination.summary.contact.email')}
          unavailable
          testIdPrefix={testIdPrefix}
        />
      )}
      <div className="space-y-1.5">
        <Label htmlFor={id} className="block text-start">
          {t('admin.destination.contactLabel')}
        </Label>
        <NativeSelect
          id={id}
          value={offered ? method : ''}
          placeholder={offered ? undefined : t('admin.destination.contactLabel')}
          options={CONTACT_CHOICES.map((choice) => ({
            value: choice,
            label: t(METHOD_KEYS[choice]),
          }))}
          onChange={(next) => {
            if (next !== '') onChange(next as WebsiteContactMethod);
          }}
        />
      </div>
    </div>
  );
}

export function CollectionSelect({
  client,
  storeId,
  collectionRef,
  unavailable,
  promptTone = 'neutral',
  onChange,
  testIdPrefix,
}: {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly collectionRef: string;
  readonly unavailable: boolean;
  readonly promptTone?: PickerPromptTone;
  readonly onChange: (collectionRef: string) => void;
  readonly testIdPrefix: string;
}): React.JSX.Element {
  const { t, lang } = useTranslation();
  const id = useId();
  const query = useStorefrontCustomProductCollectionPickerOptions(client, { storeId, search: '' });
  const options = (query.data ?? [])
    .filter((collection) => collection.isSelectable)
    .map((collection) => ({
      value: collection.collectionRef,
      label: collection.name[lang] || collection.name.en || collection.collectionRef,
    }));
  const shown = options.some((option) => option.value === collectionRef) ? collectionRef : '';
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-collection`}>
      {unavailable && collectionRef !== '' ? (
        <PickerSelection
          text={t('admin.destination.selectedUnknown')}
          unavailable
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {!query.isLoading && !query.isError && options.length === 0 ? (
        <p
          className="text-muted-foreground rounded-lg border border-dashed p-4 text-start text-sm"
          data-testid={`${testIdPrefix}-collection-empty`}
        >
          {t('admin.destination.noCollections')}
        </p>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor={id} className="block text-start">
            {t('admin.destination.chooseCollection')}
          </Label>
          <NativeSelect
            id={id}
            value={shown}
            placeholder={t('admin.destination.chooseCollection')}
            options={options}
            disabled={query.isLoading}
            onChange={(next) => {
              if (next !== '' && next !== collectionRef) onChange(next);
            }}
          />
          {/* The placeholder already prompts; it turns into an error only on a finish attempt. */}
          {collectionRef === '' && promptTone === 'error' && options.length > 0 ? (
            <PickerPrompt
              text={t('admin.destination.pickCollection')}
              tone="error"
              testId={`${testIdPrefix}-collection-prompt`}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
